import React, { useState } from 'react';
import { Card } from '@app/shared/components/card/Card';
import { CardCornersVariation, CardVariation } from '@app/shared/components/card/card-enums';
import { useAppSelector } from '@app/core/hooks/redux-hooks';
import { selectMaintenanceModeSettings } from '@app/core/store/ui-values-slice-selectors';
import { dateFormatter } from '@app/shared/formatters';
import SvgWarning from '@app/shared/icons/Warning';
import styles from './under-maintenance.module.scss';
import SvgXMark from '@app/shared/icons/XMark';
import Button from '@app/shared/components/button/Button';
import { ButtonAppearance, ButtonSize } from '@app/shared/components/button/button-enums';
import { useLocale } from '@app/core/hooks/useLocale';
import { useMaintenanceMode } from '../inputs/opm-inputs-2/utils/useMaintenanceMode';
import { calculateTimeDifferenceInHours } from '@app/shared/helpers/maintenance/date-time-calculations';

const PARENT_CLASSNAME = 'maintenance-card';

const MaintenanceCard: React.FC = () => {
  const { l } = useLocale();
  const maintenanceModeSettings = useAppSelector(selectMaintenanceModeSettings);
  const { renderCard } = useMaintenanceMode();
  const [closeMaintenanceNotice, setCloseMaintenanceNotice] = useState(false);

  const closeMaintenanceNoticeHandler = () => {
    setCloseMaintenanceNotice(true);
  };

  const formattedStartDate = maintenanceModeSettings.start
    ? dateFormatter(maintenanceModeSettings.start, {
        dateStyle: 'full',
      })
    : l('_NotAvailable');

  const formattedStartTime = maintenanceModeSettings.start
    ? dateFormatter(maintenanceModeSettings.start, {
        timeStyle: 'short',
      })
    : l('_NotAvailable');

  const formattedCompletionHours =
    maintenanceModeSettings.start && maintenanceModeSettings.end
      ? Math.ceil(
          calculateTimeDifferenceInHours(
            new Date(maintenanceModeSettings.start),
            new Date(maintenanceModeSettings.end)
          )
        )
      : null;

  const formattedCompletionTime =
    formattedCompletionHours !== null
      ? l('_Hours', { hours: formattedCompletionHours })
      : l('_NotAvailable');

  if (renderCard && !closeMaintenanceNotice) {
    return (
      <>
        <Card
          hasShadow
          cornersVariation={CardCornersVariation.Secondary}
          variation={CardVariation.TertiarySpacing}
          className={styles[`${PARENT_CLASSNAME}__card`]}>
          <div className={styles[`${PARENT_CLASSNAME}__card-contents`]}>
            <h2 className={styles[`${PARENT_CLASSNAME}__card-header`]}>
              {l(`_TemporaryDowntimeAlert`)}
              <SvgWarning className={styles[`${PARENT_CLASSNAME}__icons`]} />
            </h2>
            <Button
              appearance={ButtonAppearance.CLEAN}
              size={ButtonSize.SMALL}
              onClick={closeMaintenanceNoticeHandler}>
              <SvgXMark className={styles[`${PARENT_CLASSNAME}__icons`]} />
            </Button>
          </div>
          <div className={styles['text-content']}>
            <p>
              {l('_MaintenanceCardMessage', {
                formattedStartDate: formattedStartDate,
                formattedStartTime: formattedStartTime,
                formattedCompletionTime: formattedCompletionTime,
                releaseVersion: maintenanceModeSettings.version,
              })}
            </p>
            <br />
            <p>{l('_MaintenanceMessage')}</p>
            {maintenanceModeSettings.message && (
              <>
                <br />
                <p>{maintenanceModeSettings.message}</p>
              </>
            )}
          </div>
        </Card>
      </>
    );
  }

  return null;
};

export default MaintenanceCard;
